import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Lock, ArrowRight, ExternalLink, Mail } from "lucide-react";
import { minPlanFor, PLAN_GUARDIAN, PLAN_SENTINEL } from "../lib/usePlan";
import { UPGRADE_COPY } from "./upgradeCopy";

// Re-exported so existing `import { UPGRADE_COPY } from "./UpgradeModal"`
// call sites keep resolving — the values now live in ./upgradeCopy.js (a
// JSX-free, router-free module) so they stay importable from unit tests
// without pulling in react-router-dom transitively.
export { UPGRADE_COPY } from "./upgradeCopy";

/**
 * UpgradeModal — appears when a user clicks a locked nav item or any other
 * gated entry point. Stateless: parent controls open / onClose / feature.
 *
 * Props:
 *   open           : boolean — controlled visibility
 *   onClose        : () => void
 *   feature        : string  — the feature id (e.g. "crosscheck")
 *   Icon           : Lucide component — typically forwarded from the nav item
 */
export default function UpgradeModal({ open, onClose, feature, Icon }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const required = minPlanFor(feature || "");
  const copy = UPGRADE_COPY[feature] || {};

  const planLabel =
    required === PLAN_SENTINEL
      ? t("upgrade.plan_label_sentinel")
      : required === PLAN_GUARDIAN
      ? t("upgrade.plan_label_guardian")
      : t("upgrade.plan_label_insight");

  const bullets = copy.bulletsKey
    ? (t(copy.bulletsKey, { returnObjects: true }) || [])
    : [];

  const subject = encodeURIComponent(
    t("upgrade.email_subject", { plan: planLabel }),
  );
  const mailto = `mailto:upgrade@qenva.com?subject=${subject}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-zinc-200"
        data-testid="upgrade-modal"
      >
        {/* Header with feature icon + plan ribbon */}
        <div className="relative bg-gradient-to-br from-qenva-navy to-qenva-navy-soft px-6 pt-7 pb-5 text-white">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 grid place-items-center shrink-0">
              {Icon
                ? <Icon size={20} strokeWidth={1.75} className="text-qenva-blue" />
                : <Lock size={18} strokeWidth={1.75} className="text-qenva-blue" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-qenva-blue mb-1">
                {t("upgrade.kicker", { plan: planLabel })}
              </p>
              <DialogHeader>
                <DialogTitle className="text-[15.5px] text-white leading-snug">
                  {copy.titleKey ? t(copy.titleKey) : t("upgrade.default_title")}
                </DialogTitle>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Bullets */}
        <div className="px-6 py-5 space-y-3" data-testid="upgrade-modal-bullets">
          <DialogDescription className="sr-only">
            {t("upgrade.aria_description")}
          </DialogDescription>
          {Array.isArray(bullets) && bullets.map((b, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2.5 text-[13px] text-zinc-700 leading-relaxed"
            >
              <ArrowRight size={13} className="text-qenva-cat-financing shrink-0 mt-1" />
              <span>{b}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 pt-1 flex flex-col gap-2">
          <a
            href={mailto}
            data-testid="upgrade-modal-cta"
            className="h-10 rounded-lg bg-qenva-navy text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-qenva-navy-soft transition-colors"
          >
            <Mail size={14} />
            {t("upgrade.cta_primary", { plan: planLabel })}
            <ArrowRight size={14} />
          </a>
          <button
            type="button"
            onClick={() => { onClose?.(); navigate("/pricing"); }}
            data-testid="upgrade-modal-secondary"
            className="h-10 rounded-lg border border-zinc-300 text-zinc-700 text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors"
          >
            <ExternalLink size={13} />
            {t("upgrade.cta_secondary")}
          </button>
          <p className="text-[11px] text-zinc-500 text-center mt-1">
            {t("upgrade.contact_promise")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
